import { useMemo, useState } from 'preact/hooks';
import type Shipment from 'interfaces/PmParcel';
import { useGlobalConfigContext } from 'GlobalConfig.context';
import type { Carrier } from 'interfaces/Carrier.interface';
import {
  useActiveCarrier,
  useCarrierOverride,
  useFinalCarrierInfo,
  useInitialCarrier,
} from './useCarrierData';
import { useSubParcel } from './useSubParcel.hook';

export interface CarrierDisplayInfo {
  isShowingSwitcher: boolean;
  isShowCurrentCarrier: boolean;
  isCurrentCarrierAsDestinationCarrier: boolean;
  switchCarrier: () => void;
  carrierInfo: Carrier;
  isLoading: boolean;
}

/**
 * Main hook that orchestrates carrier information display logic
 *
 * @param shipment - Shipment data
 * @param shouldShowDestinationCarrier - Flag to enable destination carrier display
 * @returns CarrierDisplayInfo
 *
 * Terms:
 *  - initialCarrier: What shows BY DEFAULT (follows business priority)
 *  - toggledCarrier: What shows AFTER USER CLICKS switcher
 *  - fallbackCarrier: Always Main Carrier OR Org Carrier
 *  - activeCarrier: Currently rendered carrier
 *
 * Business Rule: Show special carrier by default (Linked-Shipment or DC),
 * but let user toggle to Fallback Carrier to see original tracking.
 *
 * Ref: https://parcelperform.atlassian.net/wiki/spaces/TE/pages/4494229537/Carrier+information+display+logic
 */
export const useCarrierInformationDisplay = (
  shipment: Shipment,
  shouldShowDestinationCarrier = false,
): CarrierDisplayInfo => {
  const globalConfig = useGlobalConfigContext();
  const [isUserViewingToggled, setIsUserViewingToggled] = useState(false);

  // Step 1: Compute initial carrier selection based on business rules
  const initialState = useInitialCarrier(
    shipment,
    shouldShowDestinationCarrier,
  );

  // Step 2: Compute active carrier logic and determine which is currently shown
  const {
    isActiveCarrierDestination,
    isCurrentCarrierAsDestinationCarrier,
    isActiveCarrierLinkedShipment,
    activeCarrier,
  } = useActiveCarrier(
    initialState,
    isUserViewingToggled,
    shouldShowDestinationCarrier,
  );

  // Step 3: Fetch sub-parcel data for destination carrier if needed
  const { data: subParcelTrackingNumber, isLoading: isLoadingSubParcel } =
    useSubParcel({
      shipmentUuid: shipment.parcelperformId,
      organizationId: globalConfig?.organizationId,
      destinationCarrierId: shipment.destinationCarrier?.id,
      enabled:
        isActiveCarrierDestination &&
        !!shipment.parcelperformId &&
        !!globalConfig?.organizationId,
    });

  // Step 4: Resolve carrier config with OCC overrides
  const carrierOverride = useCarrierOverride(
    activeCarrier,
    shipment.carrierOrg,
    isActiveCarrierLinkedShipment,
    initialState.linkedShipmentCarrierOrg,
  );

  // Step 5: Resolve parcel ID for URL placeholder
  const activeParcelId = useMemo(() => {
    if (isActiveCarrierDestination) {
      return subParcelTrackingNumber || '';
    }
    return shipment.parcelId;
  }, [isActiveCarrierDestination, subParcelTrackingNumber, shipment.parcelId]);

  // Step 6: Build final carrier info with all data
  const carrierInfo: Carrier = useFinalCarrierInfo(
    activeCarrier,
    carrierOverride,
    activeParcelId,
    shipment,
  );

  // Loading state: Only when active carrier is DC and waiting for sub-parcel
  const isLoading = isActiveCarrierDestination && isLoadingSubParcel;

  // Carrier switcher toggle
  const switchCarrier = () => setIsUserViewingToggled((prev) => !prev);

  return {
    isShowingSwitcher: initialState.isShowingSwitcher,
    isShowCurrentCarrier:
      isActiveCarrierDestination || isActiveCarrierLinkedShipment,
    isCurrentCarrierAsDestinationCarrier,
    isLoading,
    carrierInfo,
    switchCarrier,
  };
};

// Alias export for backward compatibility
export const useCarrierInformation = useCarrierInformationDisplay;
